import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  CheckboxLineField,
  SequenceMenu,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import { useState } from 'react'
import { withoutContractReferences } from 'modules/BillingSchedules/utils/withoutContractReferences'

const FinalizeInvoiceModal = (props: {
  invoiceId: string
  onSuccess?: () => void
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()
  const [autoIssue, setAutoIssue] = useState(false)

  const invoiceQuery = useQuery(dashboard20240730Client.getInvoice, {
    id: props.invoiceId
  })

  const customerQuery = useQuery(
    dashboard20240730Client.getCustomer,
    {
      id: invoiceQuery.data?.customerId ?? ''
    },
    {
      enabled: Boolean(invoiceQuery.data?.customerId)
    }
  )
  const billingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: invoiceQuery.data?.billingScheduleId ?? ''
    },
    {
      enabled: Boolean(invoiceQuery.data?.billingScheduleId)
    }
  )

  const updateBillingScheduleMutation = useMutation(
    dashboard20240730Client.putBillingSchedule
  )
  const finalizeMutation = useMutation(
    dashboard20240730Client.postFinalizeInvoice,
    {
      onSuccess: () => {
        displayNotification('Invoice finalized', {
          type: 'success'
        })
      },
      onError: () => {
        displayNotification('Failed to finalize invoice', {
          type: 'error'
        })
      }
    }
  )

  const isLoading = Boolean(
    invoiceQuery.isPending ||
      customerQuery.isPending ||
      (invoiceQuery.data?.billingScheduleId && billingScheduleQuery.isPending)
  )

  const showAutoInvoiceCheckbox =
    billingScheduleQuery.data && !billingScheduleQuery.data.autoIssueInvoices

  return (
    <SimpleModal
      data-testid="invoices.finalizeInvoice.modal"
      variant="dialog"
      title="Finalize invoice"
      submitButtonText="Finalize invoice"
      submittingButtonText="Finalizing..."
      isLoading={isLoading}
      isOpen
      onClose={props.onClose}
      onSubmit={async () => {
        await Promise.all([
          finalizeMutation.mutateAsync({ id: props.invoiceId }),
          autoIssue && billingScheduleQuery.data
            ? updateBillingScheduleMutation.mutateAsync({
                id: billingScheduleQuery.data.id,
                body: {
                  ...withoutContractReferences(billingScheduleQuery.data),
                  autoIssueInvoices: true
                }
              })
            : Promise.resolve()
        ])

        props.onSuccess?.()
        return true
      }}
    >
      <Flex flexDirection="column" gap="16px">
        {`Finalize this invoice for ${customerQuery.data?.legalName}. After finalizing you can no longer edit the invoice. To make further changes, use the Credit note feature.`}

        {showAutoInvoiceCheckbox && (
          <CheckboxLineField
            data-testid="invoices.finalizeInvoice.autoIssueCheckbox"
            value={autoIssue}
            onChange={newValue => setAutoIssue(newValue)}
            text="Auto-send future invoices created by this Billing Schedule"
          />
        )}
      </Flex>
    </SimpleModal>
  )
}

export const FinalizeInvoice = (props: {
  invoiceId: string
  onSuccess?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { can } = usePermissions()

  if (!can('org.invoice:finalize')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        data-testid="invoices.finalizeInvoice.menuItem"
        uuid="finalize-invoice"
        label="Finalize invoice"
        onClick={e => {
          e.stopPropagation()
          setIsOpen(true)
        }}
      />
      {isOpen && (
        <FinalizeInvoiceModal
          invoiceId={props.invoiceId}
          onSuccess={props.onSuccess}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
